import React, { useEffect, useState, useCallback } from "react";
import api from "../axiosSetup";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "../css/marketplace-dark.css";

const ATTENDANCE_META = {
  anwesend:       { color: "#22c55e", label: "Anwesend" },
  entschuldigt:   { color: "#60a5fa", label: "Entschuldigt" },
  unentschuldigt: { color: "#ef4444", label: "Unentschuldigt" },
};

function AttendanceBadge({ status }) {
  const m = ATTENDANCE_META[status];
  if (!m) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
      border: `1px solid ${m.color}`, color: m.color,
      background: `${m.color}18`,
    }}>
      {m.label}
    </span>
  );
}

function DateList({ dates, limit = 5 }) {
  const [expanded, setExpanded] = useState(false);
  if (!dates || dates.length === 0)
    return <div className="text-white-50" style={{ fontSize: 13 }}>Keine Termine</div>;

  const shown = expanded ? dates : dates.slice(0, limit);
  return (
    <>
      <ul className="m-0 ps-3" style={{ fontSize: 13 }}>
        {shown.map((d, i) => (
          <li key={i} style={{ color: "rgba(255,255,255,0.75)", lineHeight: 1.8 }}>
            {dayjs(d.workDate).format("DD.MM.YYYY")} &nbsp;
            {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
          </li>
        ))}
      </ul>
      {dates.length > limit && (
        <button
          className="btn btn-link p-0 mt-1"
          style={{ fontSize: 12, color: "#94a3b8" }}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? "Weniger anzeigen" : `+ ${dates.length - limit} weitere`}
        </button>
      )}
    </>
  );
}

export default function SRVMyWorkAssignments() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]     = useState("upcoming"); // "upcoming" | "history"

  const token = localStorage.getItem("authToken");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/swap-marketplace/my-assignments", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Fehler beim Laden der Einsätze.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const upcoming = items.filter(a => a.upcomingDates.length > 0);
  const history  = items.filter(a => a.upcomingDates.length === 0);
  const list     = view === "upcoming" ? upcoming : history;

  const TABS = [
    { key: "upcoming", label: "Anstehend",  count: upcoming.length },
    { key: "history",  label: "Vergangen",  count: history.length  },
  ];

  return (
    <div className="marketplace-dark">
      {/* Mobil: scrollbare Pill-Buttons */}
      <div
        className="d-md-none mb-4"
        style={{
          display: "flex",
          overflowX: "auto",
          gap: 8,
          paddingBottom: 4,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {TABS.map(({ key, label, count }) => {
          const active = view === key;
          return (
            <button
              key={key}
              onClick={() => setView(key)}
              style={{
                flexShrink: 0,
                padding: "9px 18px",
                borderRadius: 999,
                border: active ? "2px solid #facc15" : "2px solid rgba(255,255,255,0.2)",
                background: active ? "#facc15" : "rgba(255,255,255,0.06)",
                color: active ? "#111" : "rgba(255,255,255,0.75)",
                fontWeight: active ? 700 : 500,
                fontSize: 14,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background .15s, color .15s, border-color .15s",
              }}
            >
              {label}
              {!loading && (
                <span style={{
                  marginLeft: 6,
                  background: active ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.2)",
                  borderRadius: 999,
                  padding: "1px 7px",
                  fontSize: 12,
                  fontWeight: 700,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}

        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            flexShrink: 0,
            padding: "9px 14px",
            borderRadius: 999,
            border: "2px solid rgba(255,255,255,0.2)",
            background: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.6)",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          ↺
        </button>
      </div>

      {/* Desktop: Underline-Tabs */}
      <div
        className="d-none d-md-flex align-items-center justify-content-between mb-4"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
      >
        <ul className="nav" style={{ gap: 4 }}>
          {TABS.map(({ key, label, count }) => {
            const active = view === key;
            return (
              <li className="nav-item" key={key}>
                <button
                  className="nav-link"
                  onClick={() => setView(key)}
                  style={{
                    color: active ? "#facc15" : "rgba(255,255,255,0.6)",
                    background: "none",
                    border: "none",
                    borderBottom: active ? "2px solid #facc15" : "2px solid transparent",
                    borderRadius: 0,
                    fontWeight: active ? 700 : 400,
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "color .15s, border-color .15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                  {!loading && (
                    <span style={{
                      marginLeft: 6,
                      background: active ? "rgba(250,204,21,0.2)" : "rgba(255,255,255,0.1)",
                      borderRadius: 999,
                      padding: "1px 7px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: active ? "#facc15" : "rgba(255,255,255,0.5)",
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <button
          className="btn btn-sm btn-outline-light mb-2"
          onClick={fetchData}
          disabled={loading}
        >
          Aktualisieren
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <p className="text-white-50">Lade Einsätze…</p>
      ) : list.length === 0 ? (
        <div style={{
          padding: "24px", borderRadius: 12, textAlign: "center",
          border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)",
        }}>
          {view === "upcoming"
            ? "Keine anstehenden Arbeitseinsätze."
            : "Keine vergangenen Arbeitseinsätze gefunden."}
        </div>
      ) : (
        <div className="row g-3">
          {list.map(a => {
            const dates       = view === "upcoming" ? a.upcomingDates : a.pastDates;
            const nextDate    = a.upcomingDates[0];

            return (
              <div className="col-12 col-lg-6" key={a.assignmentId}>
                <div style={{
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  padding: "16px 20px",
                  height: "100%",
                }}>
                  {/* Kopf */}
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div className="fw-semibold" style={{ fontSize: 16 }}>{a.name}</div>
                    <AttendanceBadge status={a.attendanceStatus} />
                  </div>

                  {/* Nächster Termin (nur bei Anstehend) */}
                  {view === "upcoming" && nextDate && (
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "4px 10px", borderRadius: 8, marginBottom: 10,
                      background: "rgba(250,204,21,0.1)",
                      border: "1px solid rgba(250,204,21,0.3)",
                      color: "#fde68a", fontSize: 13, fontWeight: 600,
                    }}>
                      Nächster Termin: {dayjs(nextDate.workDate).format("DD.MM.YYYY")},&nbsp;
                      {String(nextDate.timeFrom).slice(0, 5)} Uhr
                    </div>
                  )}

                  {/* Termine */}
                  <div className="mb-2">
                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
                      {view === "upcoming" ? "Anstehende Termine:" : "Vergangene Termine:"}
                    </div>
                    <DateList dates={dates} />
                  </div>

                  {/* Anmerkung des Mitglieds */}
                  {a.attendanceComment && (
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 6 }}>
                      Anmerkung: {a.attendanceComment}
                    </div>
                  )}

                  {/* Verantwortliche Person */}
                  {a.responsiblePerson && (
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", marginTop: 6 }}>
                      Verantwortlich: {a.responsiblePerson}
                    </div>
                  )}

                  {/* Bemerkung */}
                  {a.remark && (
                    <div style={{
                      marginTop: 8, padding: "6px 10px", borderRadius: 8,
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      fontSize: 13, color: "rgba(255,255,255,0.6)",
                    }}>
                      {a.remark}
                    </div>
                  )}

                  {/* Belegung */}
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                    {a.bookedCount} / {a.numberOfPeople} Personen eingetragen
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
